import { forwardRef } from "react";
import logo from "../../assets/img/logos/logoColor.png";
import { formatMonto } from "../../utils/rentalCalculations";
import { montoEnLetras } from "../../utils/numeroALetras";

// "2026-06-29" -> { dia: "29", mes: "06", anio: "2026" }
const partesFecha = (iso) => {
  const [y, m, d] = String(iso || "").split("-");
  return { dia: d || "", mes: m || "", anio: y || "" };
};

// Línea de formulario con etiqueta + valor subrayado, al estilo del recibo papel.
const Linea = ({ label, value, className = "" }) => (
  <div className={`flex items-baseline gap-1.5 ${className}`}>
    <span className="text-[10px] text-gray-500 whitespace-nowrap">{label}</span>
    <span className="flex-1 border-b border-dotted border-gray-400 text-[12px] font-semibold text-gray-800 min-h-[16px] leading-tight">
      {value || " "}
    </span>
  </div>
);

// Recibo de cobro de alquiler, replicando el talonario de la inmobiliaria.
// Es lo que se captura como imagen para compartir por WhatsApp.
const ReciboCard = forwardRef(function ReciboCard(
  { nombre, propiedad, periodo, monto, moneda = "ARS", fecha, numero, domicilio, localidad, cuit, observaciones, firmaImg, aclaracion },
  ref
) {
  const { dia, mes: mesNum, anio: anioNum } = partesFecha(fecha);

  return (
    <div
      ref={ref}
      style={{ width: 460 }}
      className="bg-white text-gray-800 p-5 rounded-lg border border-gray-200"
    >
      {/* Encabezado */}
      <div className="flex items-start justify-between pb-2 border-b-2 border-red-600">
        <div className="flex items-center gap-2.5">
          <img
            src={logo}
            alt="Hugo A. Plorutti y Cía"
            className="h-14 w-auto object-contain"
          />
          <div className="leading-tight">
            <p className="text-[9px] text-gray-600">Arenales 192</p>
            <p className="text-[9px] text-gray-600">2241460121</p>
            <p className="text-[9px] text-gray-600">ploruttia@hotmail.com</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-2xl font-black tracking-wide text-gray-800 leading-none">RECIBO</p>
          <p className="text-[7px] text-gray-500 uppercase mt-0.5">Documento no válido como factura</p>
          {numero && (
            <p className="text-[12px] font-bold text-red-700 mt-1">N° {numero}</p>
          )}
        </div>
      </div>

      {/* Fecha */}
      <div className="flex justify-end gap-1 mt-2 mb-1">
        {[
          { v: dia, l: "DÍA" },
          { v: mesNum, l: "MES" },
          { v: anioNum.slice(2), l: "AÑO" },
        ].map((b, i) => (
          <div key={i} className="text-center">
            <div className="border border-gray-400 rounded px-2 py-0.5 text-[12px] font-semibold text-gray-800 min-w-[28px]">
              {b.v || " "}
            </div>
            <p className="text-[7px] text-gray-400 mt-0.5">{b.l}</p>
          </div>
        ))}
      </div>

      {/* Datos del cliente */}
      <div className="space-y-2 mt-1">
        <Linea label="Nombre y Apellido" value={nombre} />
        <div className="flex gap-3">
          <Linea label="Domicilio" value={domicilio} className="flex-1" />
          <Linea label="Localidad" value={localidad} className="flex-1" />
        </div>
        {cuit && <Linea label="CUIT N°" value={cuit} />}
      </div>

      {/* Concepto */}
      <div className="mt-3 space-y-2">
        <div>
          <p className="text-[10px] text-gray-500">Recibí la cantidad de pesos</p>
          <p className="border-b border-dotted border-gray-400 text-[12px] font-semibold text-gray-800 leading-snug pb-0.5">
            {montoEnLetras(monto)}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-gray-500">en concepto de cancelación de alquiler correspondiente al mes de</p>
          <p className="border-b border-dotted border-gray-400 text-[12px] font-semibold text-gray-800 leading-tight pb-0.5 min-h-[16px]">
            {periodo}
          </p>
        </div>
        <Linea label="inmueble sito en" value={propiedad} />
      </div>

      {observaciones && (
        <p className="text-[10px] text-gray-500 mt-2">
          <span className="font-medium">Observaciones:</span> {observaciones}
        </p>
      )}

      {/* Firma + Total, en la misma línea */}
      <div className="flex items-end justify-between gap-4 mt-4 pt-2 border-t border-gray-200">
        <div className="text-center">
          {firmaImg && (
            <img
              src={firmaImg}
              alt="Firma"
              className="h-12 w-auto object-contain mx-auto -mb-1"
            />
          )}
          <div className="border-t border-gray-400 w-44" />
          {aclaracion && (
            <p className="text-[11px] font-semibold text-gray-700 mt-1">{aclaracion}</p>
          )}
          <p className="text-[9px] text-gray-500">Firma y aclaración</p>
        </div>
        <div className="text-right shrink-0 pb-1">
          <span className="text-[12px] font-bold text-gray-600">TOTAL</span>
          <p className="text-2xl font-extrabold text-gray-900 leading-none mt-0.5">
            {formatMonto(monto, moneda)}
          </p>
        </div>
      </div>
    </div>
  );
});

export default ReciboCard;
